/**
 * Knowledge Hub React Query Hooks
 *
 * Provides useQuery / useMutation hooks for knowledge hub CRUD operations.
 * Uses getOttoToken() for dataplane authentication and derives the project name
 * from the selectedApp in appStore.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { useAppStore } from '@/stores/appStore';
import type {
  KnowledgeHub,
  KnowledgeArtifact,
  CreateFileHubRequest,
  CreateFoundryIQHubRequest,
  CreateAISearchHubRequest,
} from '@/types/knowledgeHub';
import {
  listKnowledgeHubs,
  listArtifacts,
  createFileHub,
  createFoundryIQHub,
  createAISearchHub,
  uploadArtifacts,
  deleteKnowledgeHub,
  deleteKnowledgeHubs,
  deleteArtifact,
  KnowledgeHubApiError,
} from '@/services/knowledgeHubService';
import { useNotificationStore } from '@/stores/notificationStore';

// =============================================================================
// QUERY KEYS
// =============================================================================

export const knowledgeHubKeys = {
  all: ['knowledgeHubs'] as const,
  list: (projectName: string) =>
    [...knowledgeHubKeys.all, 'list', projectName] as const,
  artifacts: (projectName: string, hubName: string) =>
    [...knowledgeHubKeys.all, 'artifacts', projectName, hubName] as const,
};

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Get the project name from the selected app or the selected project in the store.
 * Returns null if neither is available.
 */
function useProjectName(): string | null {
  const selectedApp = useAppStore((s) => s.selectedApp);
  const selectedProject = useAppStore((s) => s.selectedProject);
  return selectedApp?.projectName ?? selectedProject ?? null;
}

/**
 * Notify the user when a knowledge hub API call fails.
 * 4xx errors show the server-provided message; other errors use the available
 * message when possible, with a generic fallback if none is provided.
 */
function notifyApiError(title: string, error: Error): void {
  const message =
    error instanceof KnowledgeHubApiError
      ? error.message
      : error.message?.trim() || 'An unexpected error occurred. Please try again.';

  useNotificationStore.getState().addNotification({
    type: 'error',
    title,
    message,
  });
}

// =============================================================================
// QUERY HOOKS
// =============================================================================

/**
 * Fetch all knowledge hubs for the current automation project.
 */
export function useKnowledgeHubs(): UseQueryResult<KnowledgeHub[], Error> {
  const { getOttoToken } = useAuth();
  const projectName = useProjectName();

  return useQuery({
    queryKey: knowledgeHubKeys.list(projectName ?? ''),
    queryFn: async () => {
      const token = await getOttoToken();
      if (!token) throw new Error('Failed to get access token');
      return listKnowledgeHubs(token, projectName!);
    },
    enabled: !!projectName,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Fetch artifacts for a specific knowledge hub.
 * @deprecated Artifacts are now returned inline with hubs via listKnowledgeHubs().
 * Use hub.artifacts directly instead of this hook.
 * Kept for backward compatibility.
 */
export function useKnowledgeArtifacts(hubName: string | null): UseQueryResult<KnowledgeArtifact[], Error> {
  const { getOttoToken } = useAuth();
  const projectName = useProjectName();

  return useQuery({
    queryKey: knowledgeHubKeys.artifacts(projectName ?? '', hubName ?? ''),
    queryFn: async () => {
      const token = await getOttoToken();
      if (!token) throw new Error('Failed to get access token');
      return listArtifacts(token, projectName!, hubName!);
    },
    enabled: !!projectName && !!hubName,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Helper to get artifacts for a hub from the cached hub data.
 * Use this instead of useKnowledgeArtifacts to avoid an extra API call.
 */
export function getHubArtifacts(
  hubs: KnowledgeHub[],
  hubName: string | null
): KnowledgeArtifact[] {
  if (!hubName) return [];
  const hub = hubs.find((h) => h.name === hubName);
  return hub?.artifacts ?? [];
}

// =============================================================================
// MUTATION HOOKS
// =============================================================================

/**
 * Create a file-based knowledge hub and optionally upload files.
 */
export function useCreateFileHub(): UseMutationResult<
  KnowledgeHub,
  Error,
  { request: CreateFileHubRequest; files?: File[]; fileDescriptions?: Record<string, string> }
> {
  const { getOttoToken } = useAuth();
  const projectName = useProjectName();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ request, files, fileDescriptions }) => {
      const token = await getOttoToken();
      if (!token) throw new Error('Failed to get access token');
      if (!projectName) throw new Error('No project context available');

      const hub = await createFileHub(token, projectName, request);

      // Upload files if provided
      if (files && files.length > 0) {
        await uploadArtifacts(token, projectName, request.hubName, files, fileDescriptions);
      }

      return hub;
    },
    onSuccess: () => {
      if (projectName) {
        queryClient.invalidateQueries({
          queryKey: knowledgeHubKeys.list(projectName),
        });
      }
    },
    onError: (error) => notifyApiError('Failed to create knowledge hub', error),
  });
}

/**
 * Create a FoundryIQ-connected knowledge hub.
 */
export function useCreateFoundryIQHub(): UseMutationResult<
  KnowledgeHub,
  Error,
  CreateFoundryIQHubRequest
> {
  const { getOttoToken } = useAuth();
  const projectName = useProjectName();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request) => {
      const token = await getOttoToken();
      if (!token) throw new Error('Failed to get access token');
      if (!projectName) throw new Error('No project context available');
      return createFoundryIQHub(token, projectName, request);
    },
    onSuccess: () => {
      if (projectName) {
        queryClient.invalidateQueries({
          queryKey: knowledgeHubKeys.list(projectName),
        });
      }
    },
    onError: (error) => notifyApiError('Failed to create knowledge hub', error),
  });
}

/**
 * Create an AI Search-connected knowledge hub.
 */
export function useCreateAISearchHub(): UseMutationResult<
  KnowledgeHub,
  Error,
  CreateAISearchHubRequest
> {
  const { getOttoToken } = useAuth();
  const projectName = useProjectName();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request) => {
      const token = await getOttoToken();
      if (!token) throw new Error('Failed to get access token');
      if (!projectName) throw new Error('No project context available');
      return createAISearchHub(token, projectName, request);
    },
    onSuccess: () => {
      if (projectName) {
        queryClient.invalidateQueries({
          queryKey: knowledgeHubKeys.list(projectName),
        });
      }
    },
    onError: (error) => notifyApiError('Failed to create knowledge hub', error),
  });
}

/**
 * Upload file artifacts to an existing knowledge hub.
 */
export function useUploadArtifacts(): UseMutationResult<
  void,
  Error,
  { hubName: string; files: File[]; fileDescriptions?: Record<string, string> }
> {
  const { getOttoToken } = useAuth();
  const projectName = useProjectName();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ hubName, files, fileDescriptions }) => {
      const token = await getOttoToken();
      if (!token) throw new Error('Failed to get access token');
      if (!projectName) throw new Error('No project context available');
      return uploadArtifacts(token, projectName, hubName, files, fileDescriptions);
    },
    onSuccess: () => {
      if (projectName) {
        queryClient.invalidateQueries({
          queryKey: knowledgeHubKeys.list(projectName),
        });
      }
    },
    onError: (error) => notifyApiError('Failed to upload artifacts', error),
  });
}

/**
 * Delete a single knowledge hub.
 */
export function useDeleteKnowledgeHub(): UseMutationResult<void, Error, string> {
  const { getOttoToken } = useAuth();
  const projectName = useProjectName();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (hubName: string) => {
      const token = await getOttoToken();
      if (!token) throw new Error('Failed to get access token');
      if (!projectName) throw new Error('No project context available');
      return deleteKnowledgeHub(token, projectName, hubName);
    },
    onSuccess: () => {
      if (projectName) {
        queryClient.invalidateQueries({
          queryKey: knowledgeHubKeys.list(projectName),
        });
      }
    },
    onError: (error) => notifyApiError('Failed to delete knowledge hub', error),
  });
}

/**
 * Delete multiple knowledge hubs.
 */
export function useDeleteKnowledgeHubs(): UseMutationResult<void, Error, string[]> {
  const { getOttoToken } = useAuth();
  const projectName = useProjectName();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (hubNames: string[]) => {
      const token = await getOttoToken();
      if (!token) throw new Error('Failed to get access token');
      if (!projectName) throw new Error('No project context available');
      return deleteKnowledgeHubs(token, projectName, hubNames);
    },
    onSuccess: () => {
      if (projectName) {
        queryClient.invalidateQueries({
          queryKey: knowledgeHubKeys.list(projectName),
        });
      }
    },
    onError: (error) => notifyApiError('Failed to delete knowledge hubs', error),
  });
}

/**
 * Delete a single artifact from a knowledge hub.
 */
export function useDeleteArtifact(): UseMutationResult<
  void,
  Error,
  { hubName: string; artifactName: string }
> {
  const { getOttoToken } = useAuth();
  const projectName = useProjectName();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ hubName, artifactName }) => {
      const token = await getOttoToken();
      if (!token) throw new Error('Failed to get access token');
      if (!projectName) throw new Error('No project context available');
      return deleteArtifact(token, projectName, hubName, artifactName);
    },
    onSuccess: (_data, { artifactName }) => {
      useNotificationStore.getState().addNotification({
        type: 'info',
        title: 'Artifact deletion in progress',
        message: `"${artifactName}" is being deleted. It will be removed shortly.`,
      });
      if (projectName) {
        queryClient.invalidateQueries({
          queryKey: knowledgeHubKeys.list(projectName),
        });
      }
    },
    onError: (error) => notifyApiError('Failed to delete artifact', error),
  });
}
